import React, { useState, useEffect } from 'react';
import ResourceLayout from '../../components/ResourceLayout/ResourceLayout';
import ResourceDashboardView from '../../components/ResourceDashboard/ResourceDashboardView';
import BenchView from '../../components/Bench/BenchView';
import DeviationsView from '../../components/Deviations/DeviationsView';
import ExpiringView from '../../components/Expiring/ExpiringView';
import ProjectionsView from '../../components/Projections/ProjectionsView';
import './ResourceDashboard.css';
import MultiProjectView from '../../components/MultiProject/MultiProjectView';

// Mock data for demonstration - in a real app, this would come from your API
const mockData = {
  resourceCounts: {
    billable: 150,
    nonBillable: 45,
    shadow: 25,
    total: 220
  },
  billStatusSummary: [
    { name: 'Billable', count: 150, totalAllocation: 68, color: '#0088FE' },
    { name: 'Non-Billable', count: 45, totalAllocation: 20, color: '#00C49F' },
    { name: 'Shadow', count: 25, totalAllocation: 12, color: '#FFBB28' }
  ],
  projectSummary: [
    { project: 'Project Alpha', billable: 25, nonBillable: 5, shadow: 3, maxAllocation: 85, allocationDeviation: 5.2, status: 'active' },
    { project: 'Project Beta', billable: 30, nonBillable: 8, shadow: 2, maxAllocation: 90, allocationDeviation: 2.1, status: 'active' },
    { project: 'Project Gamma', billable: 20, nonBillable: 4, shadow: 1, maxAllocation: 75, allocationDeviation: -3.4, status: 'pending' },
    { project: 'Project Delta', billable: 35, nonBillable: 6, shadow: 4, maxAllocation: 95, allocationDeviation: 8.7, status: 'active' },
    { project: 'Project Epsilon', billable: 40, nonBillable: 12, shadow: 5, maxAllocation: 100, allocationDeviation: 12.3, status: 'critical' }
  ],
  // Mock project allocation data for enhanced Project Summary Details
  projectAllocationData: {
    data: [
      { projectName: 'Project Alpha', clientName: 'Client A', projectManagerName: 'John Doe', employeeName: 'Alice Smith', totalExperience: 18, billStatus: 'Billable', billAllocation: 80, primarySkill: 'React', stream: 'Frontend', designation: 'Senior Developer' },
      { projectName: 'Project Alpha', clientName: 'Client A', projectManagerName: 'John Doe', employeeName: 'Bob Johnson', totalExperience: 72, billStatus: 'Billable', billAllocation: 100, primarySkill: 'Node.js', stream: 'Backend', designation: 'Tech Lead' },
      { projectName: 'Project Alpha', clientName: 'Client A', projectManagerName: 'John Doe', employeeName: 'Carol Wilson', totalExperience: 6, billStatus: 'Shadow', billAllocation: 50, primarySkill: 'React', stream: 'Frontend', designation: 'Junior Developer' },
      { projectName: 'Project Beta', clientName: 'Client B', projectManagerName: 'Jane Smith', employeeName: 'David Brown', totalExperience: 36, billStatus: 'Billable', billAllocation: 90, primarySkill: 'Angular', stream: 'Frontend', designation: 'Developer' },
      { projectName: 'Project Beta', clientName: 'Client B', projectManagerName: 'Jane Smith', employeeName: 'Eve Davis', totalExperience: 84, billStatus: 'Billable', billAllocation: 100, primarySkill: 'Java', stream: 'Backend', designation: 'Senior Developer' },
      { projectName: 'Project Beta', clientName: 'Client B', projectManagerName: 'Jane Smith', employeeName: 'Frank Miller', totalExperience: 12, billStatus: 'Non-Billable', billAllocation: 60, primarySkill: 'Testing', stream: 'QA', designation: 'QA Engineer' },
      { projectName: 'Project Gamma', clientName: 'Client C', projectManagerName: 'Mike Wilson', employeeName: 'Grace Lee', totalExperience: 48, billStatus: 'Billable', billAllocation: 85, primarySkill: 'Python', stream: 'Backend', designation: 'Senior Developer' },
      { projectName: 'Project Gamma', clientName: 'Client C', projectManagerName: 'Mike Wilson', employeeName: 'Henry Taylor', totalExperience: 8, billStatus: 'Shadow', billAllocation: 40, primarySkill: 'React', stream: 'Frontend', designation: 'Intern' },
      { projectName: 'Project Delta', clientName: 'Client D', projectManagerName: 'Sarah Connor', employeeName: 'Ivy Anderson', totalExperience: 96, billStatus: 'Billable', billAllocation: 100, primarySkill: 'AWS', stream: 'DevOps', designation: 'Architect' },
      { projectName: 'Project Delta', clientName: 'Client D', projectManagerName: 'Sarah Connor', employeeName: 'Jack White', totalExperience: 24, billStatus: 'Billable', billAllocation: 80, primarySkill: 'Vue.js', stream: 'Frontend', designation: 'Developer' },
      { projectName: 'Project Epsilon', clientName: 'Client E', projectManagerName: 'Tom Harris', employeeName: 'Kate Green', totalExperience: 15, billStatus: 'Billable', billAllocation: 70, primarySkill: 'React', stream: 'Frontend', designation: 'Junior Developer' },
      { projectName: 'Project Epsilon', clientName: 'Client E', projectManagerName: 'Tom Harris', employeeName: 'Liam Clark', totalExperience: 4, billStatus: 'Shadow', billAllocation: 30, primarySkill: 'JavaScript', stream: 'Frontend', designation: 'Trainee' }
    ]
  },
  deviations: [
    { 
      project: 'Project Alpha', 
      plannedBillable: 30, 
      actualBillable: 25, 
      currentBillableCount: 25,
      allowedCount: 30,
      countDeviation: -16.7,
      billablePercentage: 76,
      currentTotalAllocation: 85.2,
      maxAllocation: 90.0,
      allocationDeviation: -5, 
      deviationType: 'shortage', 
      status: 'warning', 
      totalResources: 33, 
      deviationMessage: 'Requires additional resources' 
    },
    { 
      project: 'Project Beta', 
      plannedBillable: 28, 
      actualBillable: 30, 
      currentBillableCount: 30,
      allowedCount: 28,
      countDeviation: 7.1,
      billablePercentage: 88,
      currentTotalAllocation: 92.3,
      maxAllocation: 95.0,
      allocationDeviation: 2, 
      deviationType: 'normal', 
      status: 'normal', 
      totalResources: 40, 
      deviationMessage: 'Within acceptable range' 
    },
    { 
      project: 'Project Gamma', 
      plannedBillable: 25, 
      actualBillable: 20, 
      currentBillableCount: 20,
      allowedCount: 25,
      countDeviation: -20.0,
      billablePercentage: 80,
      currentTotalAllocation: 75.5,
      maxAllocation: 85.0,
      allocationDeviation: -5, 
      deviationType: 'shortage', 
      status: 'warning', 
      totalResources: 25, 
      deviationMessage: 'Under-allocated by 5 resources' 
    },
    { 
      project: 'Project Delta', 
      plannedBillable: 30, 
      actualBillable: 35, 
      currentBillableCount: 35,
      allowedCount: 30,
      countDeviation: 16.7,
      billablePercentage: 78,
      currentTotalAllocation: 97.8,
      maxAllocation: 100.0,
      allocationDeviation: 5, 
      deviationType: 'excess', 
      status: 'normal', 
      totalResources: 45, 
      deviationMessage: 'Over-allocated but manageable' 
    }
  ],
  expiringAllocations: [
    { 
      resource: 'John Doe', 
      project: 'Project Alpha', 
      endDate: '2024-02-15', 
      allocation: 100,
      type: 'next30days'
    },
    { 
      resource: 'Jane Smith', 
      project: 'Project Beta', 
      endDate: '2024-02-20', 
      allocation: 80,
      type: 'next30days'
    },
    { 
      resource: 'Mike Johnson', 
      project: 'Project Gamma', 
      endDate: '2024-03-10', 
      allocation: 100,
      type: 'next60days'
    }
  ],
  monthlyProjections: [
    { month: 'Jan 2024', billable: 145, nonBillable: 40, shadow: 20, total: 205 },
    { month: 'Feb 2024', billable: 150, nonBillable: 45, shadow: 25, total: 220 },
    { month: 'Mar 2024', billable: 155, nonBillable: 50, shadow: 30, total: 235 },
    { month: 'Apr 2024', billable: 160, nonBillable: 48, shadow: 28, total: 236 },
    { month: 'May 2024', billable: 165, nonBillable: 52, shadow: 32, total: 249 }
  ],
  benchResources: [
    { name: 'Alice Brown', skills: ['React', 'Node.js'], experience: '5 years', availability: '100%' },
    { name: 'Bob Wilson', skills: ['Angular', 'Python'], experience: '3 years', availability: '50%' },
    { name: 'Carol Davis', skills: ['Vue.js', 'Java'], experience: '4 years', availability: '100%' }
  ],
  benchStreams: [
    { stream: 'Frontend', count: 8 },
    { stream: 'Backend', count: 5 },
    { stream: 'Full Stack', count: 12 },
    { stream: 'DevOps', count: 3 }
  ]
};

const ResourceDashboard = () => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [data] = useState(mockData);

  useEffect(() => {
    document.title = 'Resource Dashboard - Veersa Portal';
  }, []);

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  const getCounts = () => ({
    deviations: data.deviations.filter(d => d.status !== 'normal').length,
    expiring: data.expiringAllocations.length,
    projections: data.monthlyProjections.length,
    bench: data.benchResources.length
  });

  const getDataInfo = () => {
    const counts = getCounts();
    return [
      { text: `${counts.deviations} Deviations Found`, className: 'deviation-badge' },
      { text: `${counts.expiring} Expiring Soon`, className: 'expiring-badge' },
      { text: `${counts.projections} Months Projected`, className: 'projections-badge' }
    ];
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <ResourceDashboardView
            resourceCountData={data.resourceCounts}
            billStatusSummary={data.billStatusSummary}
            projectSummary={data.projectSummary}
            projectAllocationData={data.projectAllocationData}
          />
        );
      case 'deviations':
        return (
          <DeviationsView
            deviationData={data.deviations}
          />
        );
      case 'expiring':
        return (
          <ExpiringView
            expiringResources={data.expiringAllocations}
          />
        );
      case 'projections':
        return (
          <ProjectionsView
            monthlyProjections={data.monthlyProjections}
          />
        );
      case 'bench':
        return (
          <BenchView
            benchResources={data.benchResources}
            benchStreamData={data.benchStreams}
          />
        );
      default:
        return (
          <ResourceDashboardView
            resourceCountData={data.resourceCounts}
            billStatusSummary={data.billStatusSummary}
            projectSummary={data.projectSummary}
            projectAllocationData={data.projectAllocationData}
          />
        );
    }
  };

  const getViewTitle = () => {
    switch (currentView) {
      case 'dashboard': return 'Allocation Dashboard Overview';
      case 'deviations': return 'Allocation Deviations';
      case 'expiring': return 'Expiring Allocations';
      case 'projections': return 'Monthly Resource Projections';
      case 'bench': return 'Bench Resources Management';
      default: return 'Allocation Dashboard';
    }
  };

  const getViewSubtitle = () => {
    switch (currentView) {
      case 'dashboard': return 'Comprehensive resource allocation and utilization overview';
      case 'deviations': return 'Monitor and analyze allocation vs actual resource deployment';
      case 'expiring': return 'Track resources with upcoming allocation end dates';
      case 'projections': return 'Future resource planning and trend analysis';
      case 'bench': return 'Available resources and skills inventory';
      default: return 'Resource management and analytics';
    }
  };

  return (
    <ResourceLayout
      currentView={currentView}
      onViewChange={handleViewChange}
      counts={getCounts()}
      title={getViewTitle()}
      subtitle={getViewSubtitle()}
      dataInfo={getDataInfo()}
    >
      {renderCurrentView()}
    </ResourceLayout>
  );
};

export default ResourceDashboard;